import { Employee, RightEnum, Role } from "@finway-group/shared/lib/models"
import { Substitute } from "@finway-group/shared/lib/models/user/Substitute"
import UserCompanyProfileInterface from "@finway-group/shared/lib/models/user/userCompanyProfileInterface"
import axios from "axios"
import { Dispatch } from "react"
import { NOT_SET_VALUE } from "~Shared/config/consts"
import { AuthnService, AuthzService, EmployeeService } from "~Shared/services"
import { store } from "~Shared/store"
import { syncEmployee } from "~Shared/store/actions/employee/employeeActions"
import { getCompanyProfile } from "~Shared/utils/auth.utils"
import { isRightGranted } from "~Shared/utils/helper.utils"
import { convertUnencodedParamStringToObject } from "../config/axios.utils"

export const MODEL = "users"

export const UserService = {
    getLoggedInEmployeeProfile: (): Employee => {
        const { userId, companyId } = AuthnService.getDataFromAccessToken()
        const loggedInEmployee = EmployeeService.getEmployeeById(userId)
        loggedInEmployee.setActiveCompanyProfile(companyId)
        return loggedInEmployee
    },
    getLoggedActiveCompanyProfileRole: (employee: Employee): Role | undefined => {
        const roles: Array<Role> = store.getState().roles.items
        return roles.find((role) => role._id === employee.activeCompanyProfile.roleId)
    },
    getLoggedInEmployeeRole: (): Role | undefined => {
        const { companyId, accessToken } = AuthnService.getDataFromAccessToken()
        const activeProfile = getCompanyProfile(accessToken, companyId)
        const roles: Array<Role> = store.getState().roles.items
        return roles.find((role) => role._id === activeProfile?.roleId)
    },
    isInMyTeam: (userId: string): boolean => {
        const myProfile = UserService.getLoggedInEmployeeProfile().activeCompanyProfile
        const employeeTeam = EmployeeService.getEmployeeById(userId).setActiveCompanyProfile(myProfile.companyId)?.team
        return employeeTeam === myProfile.team
    },

    //
    // API
    //

    fetch: async ({ page = 1, limit = 20, filterQueryString = "", searchQueryString = "", sortQueryString = "", controller = new AbortController(), timeout = 20000 }) => {
        const params = convertUnencodedParamStringToObject(`page=${page}&limit=${limit}${filterQueryString}${searchQueryString}${sortQueryString}`)
        const { data } = await axios.get(`/${MODEL}`, {
            signal: controller.signal,
            timeout,
            params,
        })
        data.docs = data.docs.map((e: any) => new Employee(e))
        return data as {
            docs: Array<Employee>
            page: number
            totalPages: number
            totalDocs: number
        }
    },
    fetchOneByEmail: async (email: string) => {
        const { data } = await axios.get(`/${MODEL}/email/${email}`)
        return data
    },
    fetchUserById: async (id: string) => {
        const { data } = await axios.get(`/${MODEL}/${id}`)
        return data
    },
    create: async (createEmployee: Employee) => {
        const { data } = await axios.post(`/${MODEL}`, createEmployee)
        return new Employee(data)
    },
    update: async (id: string, updateUser: Employee) => {
        const { data } = await axios.put(`/${MODEL}/${id}`, updateUser)
        return EmployeeService.mapEmployeeActiveCompany(new Employee(data))
    },
    createCompanyProfile: async (userId: string, companyId: string, companyProfile: UserCompanyProfileInterface) => {
        const { data } = await axios.post(`/${MODEL}/${userId}/companyProfiles/${companyId}`, companyProfile)
        return new Employee(data)
    },
    updateCompanyProfile: async ({
        userId,
        companyId,
        companyProfile,
        replacementApproverId,
    }: {
        userId: string
        companyId: string
        companyProfile: UserCompanyProfileInterface
        replacementApproverId?: string
    }) => {
        const { data } = await axios.put(`/${MODEL}/${userId}/companyProfiles/${companyId}`, { ...companyProfile, replacementApproverId })
        return new Employee(data)
    },
    patchCompanyProfile: async ({ userId, companyId, payload }: { userId: string; companyId: string; payload: { creditorNumber?: string; substitutes?: Array<Substitute> } }) => {
        const { data } = await axios.patch(`/${MODEL}/${userId}/companyProfiles/${companyId}`, payload)
        return EmployeeService.mapEmployeeActiveCompany(new Employee(data))
    },
    deleteCompanyProfile: async (id: string, companyId: string, replacementApproverId?: string) => {
        await axios.delete(`/${MODEL}/${id}/companyProfiles/${companyId}`, { data: { replacementApproverId } })
    },
    requestPasswordResetLink: async () => {
        const { data } = await axios.get<{ link: string }>(`/${MODEL}/password/reset-link`, { withCredentials: true })
        return data.link
    },
    requestPhoneNumberVerification: async (phoneNumber: string | undefined) => {
        await axios.post(`/${MODEL}/phone/verification`, { phoneNumber })
    },
    checkPhoneNumberVerification: async (verificationCode: string) => {
        const { data } = await axios.post(`/${MODEL}/phone/verification-check`, { verificationCode })
        return data.verified
    },
    resetTfa: async (id: string) => {
        const response = await axios.post(`/${MODEL}/${id}/reset-tfauth`)
        return EmployeeService.mapEmployeeActiveCompany(new Employee(response.data))
    },
    submitNewCompanyProfile: async ({ values, employee, dispatch }: { values: UserCompanyProfileInterface; employee: Employee; dispatch: Dispatch<any> }) => {
        const formValues = { ...values }

        const updatedUser = await UserService.createCompanyProfile(employee.id, formValues.companyId, formValues)
        dispatch(syncEmployee(updatedUser.id))
    },
    submitCompanyProfileUpdate: async ({
        values,
        employee,
        isEmployeeInSubstituteSetting,
        workflowsReplacementApproverId,
        removeSubstituteConfirmed,
        dispatch,
    }: {
        values: UserCompanyProfileInterface
        employee: Employee
        isEmployeeInSubstituteSetting: boolean
        workflowsReplacementApproverId?: string
        removeSubstituteConfirmed: boolean
        dispatch: Dispatch<any>
    }): Promise<{ replacementApproverIdRequired?: boolean; substituteRemovalConfirmationRequired?: boolean }> => {
        const formValues = { ...values }

        if (formValues.superior === NOT_SET_VALUE) {
            formValues.superior = undefined
        }
        employee.setActiveCompanyProfile(formValues.companyId)
        // If the user is being demoted to employee role, ask for a replacement user for workflows if the user is part of any workflow.
        const isUserBeingDemoted = AuthzService.isPotentialApprover(employee?.activeCompanyProfile.roleId) && !AuthzService.isPotentialApprover(formValues.roleId)
        if (isUserBeingDemoted) {
            if (!workflowsReplacementApproverId) {
                const workflowsOfUser = employee.id ? await EmployeeService.getWorkflows(employee.id) : []
                if (workflowsOfUser.length) {
                    return { replacementApproverIdRequired: true }
                }
            }

            if (!removeSubstituteConfirmed && isEmployeeInSubstituteSetting) {
                return { substituteRemovalConfirmationRequired: true }
            }
        }

        const updatedUser = await UserService.updateCompanyProfile({
            userId: employee.id,
            companyId: formValues.companyId,
            companyProfile: formValues,
            replacementApproverId: workflowsReplacementApproverId,
        })
        dispatch(syncEmployee(updatedUser.id))

        return {}
    },
    getApproverEmployeeList: (employees: Array<Employee>) =>
        employees.filter((e: Employee) => {
            const role = UserService.getLoggedActiveCompanyProfileRole(e)
            return isRightGranted(role?.rights, RightEnum.EXPENSE__BASIC__APPROVE)
        }),
}
